import Tooltip from "@corvu/tooltip";
import { createIntersectionObserver } from "@solid-primitives/intersection-observer";

import { type JSX, createEffect, createSignal } from "solid-js";

import FooterButtonGroup from "~/components/FooterButtonGroup";
import styles from "~/components/PostNav.module.scss";
import rootStyles from "~/components/Root.module.scss";
import { clickOutside } from "~/utils/clickOutside";

type Anchors = Record<string, HTMLAnchorElement>;

const promoteAnchor = (anchor: HTMLAnchorElement) => {
  anchor.classList.add(styles.highlighted);

  if (matchMedia("(width >= 1280px)").matches) {
    anchor.scrollIntoView({
      behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "nearest",
    });
  }
};

export default function PostNav(props: JSX.HTMLAttributes<HTMLElement>) {
  const [navRef, setNavRef] = createSignal<HTMLElement>();
  const [anchorsRef, setAnchorsRef] = createSignal<Anchors>({});
  const [headingsRef, setHeadingsRef] = createSignal<HTMLHeadingElement[]>([]);

  let activeIndex = -1;

  const setActiveHeading = (id: string, setPrevious = false) => {
    const activeId = `#${id}`;
    const anchors = anchorsRef();
    const keys = Object.keys(anchors);

    if (setPrevious) {
      const thisIndex = keys.indexOf(activeId);
      if (thisIndex > activeIndex) {
        return;
      }

      anchors[activeId].classList.remove(styles.highlighted);

      if (thisIndex > 0) {
        promoteAnchor(anchors[keys[thisIndex - 1]]);
      }

      activeIndex = thisIndex - 1;

      return;
    }

    if (activeIndex !== -1) {
      anchors[keys[activeIndex]].classList.remove(styles.highlighted);
    }

    promoteAnchor(anchors[activeId]);
    activeIndex = keys.indexOf(activeId);
  };

  createIntersectionObserver(
    headingsRef,
    (entries) => {
      const height = window.innerHeight / 2;

      for (const entry of entries) {
        if (entry.boundingClientRect.top < height) {
          setActiveHeading(entry.target.id);
        } else if (!entry.isIntersecting) {
          setActiveHeading(entry.target.id, true);
        }
      }
    },
    {
      rootMargin: "-50% 0px",
    },
  );

  createEffect(() => {
    const anchors: Anchors = {};

    for (const anchor of navRef()?.querySelectorAll("a") ?? []) {
      const id = decodeURIComponent(anchor.hash);
      anchors[id] = anchor;
    }

    setAnchorsRef(anchors);

    const headings: HTMLHeadingElement[] = [];

    for (const id of Object.keys(anchors)) {
      if (!id) {
        continue;
      }

      const heading = document.querySelector<HTMLHeadingElement>(id);
      if (heading) {
        headings.push(heading);
      }
    }

    setHeadingsRef(headings);
  });

  const [isVisible, setIsVisible] = createSignal(false);

  const toggleToc = () => {
    setIsVisible((previous) => !previous);
  };

  return (
    <div
      class={styles.wrapper}
      use:clickOutside={() => {
        setIsVisible(false);
      }}
      onClick={(event) => {
        if (event.target.closest("a")) {
          setIsVisible(false);
        }
      }}
    >
      <FooterButtonGroup
        classList={{
          [styles["button-group"]]: true,
          [styles.visible]: isVisible(),
        }}
      >
        <Tooltip
          placement="top"
          strategy="fixed"
          openDelay={200}
          hoverableContent={false}
          floatingOptions={{
            offset: 10,
            flip: true,
            shift: true,
          }}
        >
          <Tooltip.Trigger onClick={toggleToc}>
            <IconTablerListTree />
            <span class={rootStyles["sr-only"]}>Índice</span>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content>
              Índice
              <Tooltip.Arrow size={10} />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip>
      </FooterButtonGroup>
      <nav {...props} ref={setNavRef} />
    </div>
  );
}
